import { injectable } from "tsyringe";

export class HttpError extends Error {
  constructor(message: string) {
    super(message);
    this.name = this.constructor.name;
  }
}

@injectable()
export class HttpClient {
  public static DEFAULT_TIMEOUT = 1000;

  public async get(
    url: string,
    options: RequestInit = { mode: "cors" },
    timeout = HttpClient.DEFAULT_TIMEOUT,
  ): Promise<Response> {
    return this.fetch(url, "GET", options, timeout);
  }

  public async put<T>(
    url: string,
    data: T,
    options: RequestInit = { mode: "cors" },
  ): Promise<Response> {
    return this.fetch(url, "PUT", options, 0, data);
  }

  private async fetch<T>(
    url: string,
    method: string,
    options: RequestInit = { mode: "cors" },
    timeout = 0,
    data?: T,
  ): Promise<Response> {
    let signal: AbortSignal | undefined = undefined;
    let timeoutId: NodeJS.Timeout | number | undefined = undefined;
    if (timeout > 0) {
      const controller = new AbortController();
      timeoutId = setTimeout(() => controller.abort(), timeout);
      signal = controller.signal;
    }
    try {
      const response = await fetch(url, {
        method,
        signal,
        headers: data
          ? {
              "Content-Type": "application/json",
            }
          : undefined,
        body: data ? JSON.stringify(data) : undefined,
        ...options,
      });
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
      if (!response.ok) {
        throw new HttpError(`HTTP error ${url}! Status: ${response.status}`);
      }
      return response;
    } catch (e) {
      if (e instanceof HttpError) {
        throw e;
      }
      throw new HttpError(`HTTP error ${e} while sending request to ${url}!`);
    }
  }
}
